import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI, scheduleProactiveRefresh } from '../services/api';
import { isWebAuthnSupported, startAuthentication } from '../utils/webauthn';

const MFAVerification = () => {
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [remainingTime, setRemainingTime] = useState(30);
  const [useRecovery, setUseRecovery] = useState(false);
  const [mfaMethods, setMfaMethods] = useState([]);
  const [activeMethod, setActiveMethod] = useState('totp'); // 'totp' | 'webauthn'

  useEffect(() => {
    const mfaToken = sessionStorage.getItem('mfa_token');
    if (!mfaToken) {
      navigate('/login');
      return;
    }

    try {
      const tokenParts = mfaToken.split('.');
      if (tokenParts.length !== 3) throw new Error('Invalid token format');
    } catch {
      sessionStorage.removeItem('mfa_token');
      navigate('/login');
      return;
    }

    // Load available methods
    const storedMethods = sessionStorage.getItem('mfa_methods');
    if (storedMethods) {
      try {
        const methods = JSON.parse(storedMethods);
        setMfaMethods(methods);
        // Default to webauthn if available and supported
        if (methods.includes('webauthn') && isWebAuthnSupported()) {
          setActiveMethod('webauthn');
        }
      } catch { /* ignore parse error */ }
    }

    const timer = setInterval(() => {
      setRemainingTime((prev) => (prev <= 1 ? 30 : prev - 1));
    }, 1000);

    return () => clearInterval(timer);
  }, [navigate]);

  const handleLoginSuccess = useCallback((response) => {
    scheduleProactiveRefresh();
    sessionStorage.removeItem('mfa_token');
    sessionStorage.removeItem('mfa_methods');

    if (response.sessions_revoked > 0) {
      sessionStorage.setItem('login_message', `${response.sessions_revoked} previous session(s) were revoked due to concurrent session limits.`);
      sessionStorage.setItem('login_message_type', 'info');
    }
    // Full page reload so AuthProvider re-mounts with the new cookies
    window.location.href = '/';
  }, []);

  const handleResponse = useCallback((response) => {
    if (response.status === 'success') {
      handleLoginSuccess(response);
    } else if (response.status === 'pending_approval') {
      sessionStorage.removeItem('mfa_token');
      sessionStorage.removeItem('mfa_methods');
      sessionStorage.setItem('pending_approval', JSON.stringify({
        access_token: response.access_token,
        user_email: response.user?.email,
        user_name: response.user?.firstname
      }));
      navigate('/pending');
    } else if (response.status === 'error' || response.error) {
      setError(response.error || 'Verification failed');
    } else {
      setError('Unexpected response from server');
    }
  }, [handleLoginSuccess, navigate]);

  // TOTP / recovery code submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const response = await authAPI.verifyMFA({ mfa_code: code });
      handleResponse(response);
    } catch (err) {
      if (err.response?.status === 401) {
        const msg = err.response?.data?.msg || err.response?.data?.error || '';
        setError('Invalid code or token expired: ' + msg);
        if (msg.includes('expired')) {
          sessionStorage.removeItem('mfa_token');
          setTimeout(() => navigate('/login'), 2000);
        }
      } else {
        setError(err.message || err.error || 'Verification failed');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // WebAuthn security key flow
  const handleSecurityKey = async () => {
    setError('');
    setIsLoading(true);

    try {
      const mfaToken = sessionStorage.getItem('mfa_token');
      if (!mfaToken) throw new Error('MFA token not found');

      // 1. Get authentication options from server
      const options = await authAPI.getWebAuthnAuthenticateOptions(mfaToken);

      // 2. Prompt user to touch their key
      const credential = await startAuthentication(options);

      // 3. Verify with server
      const response = await authAPI.verifyWebAuthnAuthentication(credential, mfaToken);
      handleResponse(response);
    } catch (err) {
      if (err.name === 'NotAllowedError') {
        setError('Security key operation was cancelled or timed out.');
      } else if (err.name === 'SecurityError') {
        setError('Security key authentication failed. Make sure you are on the correct domain.');
      } else {
        setError(err.message || err.error || 'Security key authentication failed');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCodeChange = (e) => {
    if (useRecovery) {
      const value = e.target.value.replace(/[^0-9A-Fa-f-]/g, '').slice(0, 14).toUpperCase();
      setCode(value);
    } else {
      const value = e.target.value.replace(/\D/g, '').slice(0, 6);
      setCode(value);
    }
  };

  const toggleRecovery = () => {
    setCode('');
    setError('');
    setUseRecovery(!useRecovery);
  };

  const isCodeValid = useRecovery
    ? code.length === 14 && (code.match(/-/g) || []).length === 2
    : code.length === 6;

  const hasWebAuthn = mfaMethods.includes('webauthn') && isWebAuthnSupported();
  const hasTotp = mfaMethods.includes('totp') || mfaMethods.length === 0; // default to TOTP for backwards compat

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Verify Your Identity
          </h2>
        </div>

        {/* Method picker tabs — only show if both methods available */}
        {hasWebAuthn && hasTotp && (
          <div className="flex rounded-lg border border-gray-300 overflow-hidden">
            <button
              type="button"
              onClick={() => { setActiveMethod('webauthn'); setError(''); }}
              className={`flex-1 py-2 px-4 text-sm font-medium ${
                activeMethod === 'webauthn'
                  ? 'bg-primary text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              Security Key
            </button>
            <button
              type="button"
              onClick={() => { setActiveMethod('totp'); setError(''); }}
              className={`flex-1 py-2 px-4 text-sm font-medium border-l border-gray-300 ${
                activeMethod === 'totp'
                  ? 'bg-primary text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              Authenticator App
            </button>
          </div>
        )}

        {error && (
          <div className="text-red-600 text-sm text-center bg-red-50 border border-red-200 rounded-md p-3">
            {error}
          </div>
        )}

        {/* Security Key flow */}
        {activeMethod === 'webauthn' && hasWebAuthn && (
          <div className="space-y-4 text-center">
            <div className="py-8">
              <svg className="mx-auto h-16 w-16 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.75 5.25a3 3 0 013 3m3 0a6 6 0 01-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1121.75 8.25z" />
              </svg>
              <p className="mt-4 text-sm text-gray-600">
                Insert your security key and touch it when it blinks.
              </p>
            </div>
            <button
              type="button"
              onClick={handleSecurityKey}
              disabled={isLoading}
              className={`w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-md text-white ${
                isLoading ? 'bg-indigo-400 cursor-not-allowed' : 'bg-primary hover:bg-primary-hover'
              } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary`}
            >
              {isLoading ? (
                <span className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Waiting for security key...
                </span>
              ) : (
                'Use Security Key'
              )}
            </button>
          </div>
        )}

        {/* TOTP / Recovery code flow */}
        {(activeMethod === 'totp' || !hasWebAuthn) && (
          <>
            <p className="text-center text-sm text-gray-600">
              {useRecovery
                ? 'Enter one of your recovery codes (XXXX-XXXX-XXXX)'
                : 'Enter the 6-digit code from your authenticator app'}
            </p>
            {!useRecovery && (
              <p className="text-center text-sm text-gray-500">
                Code refreshes in {remainingTime} seconds
              </p>
            )}
            <form className="space-y-6" onSubmit={handleSubmit}>
              <div>
                <input
                  id="mfa-code"
                  name="code"
                  type="text"
                  pattern={useRecovery ? "[0-9A-Fa-f-]*" : "[0-9]*"}
                  inputMode={useRecovery ? "text" : "numeric"}
                  autoComplete={useRecovery ? "off" : "one-time-code"}
                  required
                  className="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary focus:border-primary sm:text-sm tracking-widest text-center"
                  placeholder={useRecovery ? "XXXX-XXXX-XXXX" : "000000"}
                  value={code}
                  onChange={handleCodeChange}
                  maxLength={useRecovery ? 14 : 6}
                />
              </div>
              <button
                type="submit"
                disabled={!isCodeValid || isLoading}
                className={`w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white ${
                  isCodeValid && !isLoading
                    ? 'bg-primary hover:bg-primary-hover'
                    : 'bg-indigo-400 cursor-not-allowed'
                } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary`}
              >
                {isLoading ? 'Verifying...' : 'Verify Code'}
              </button>
              <div className="text-center">
                <button
                  type="button"
                  onClick={toggleRecovery}
                  className="text-sm text-primary hover:text-primary-hover underline"
                >
                  {useRecovery ? 'Use authenticator code' : 'Use a recovery code'}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default MFAVerification;
